import { z } from 'zod';

/**
 * The model's request to connect one external account.
 *
 * A toolkit slug is all it may say. The account, the provider's authorization
 * URL and the moment the connection becomes active are all resolved after this
 * point by code the model does not reach, so there is nothing else here for a
 * bad input to influence.
 */
export const connectExternalAccountInputSchema = z.object({
  toolkit: z.string().min(1),
});

/**
 * What the client hands back once the user has finished.
 *
 * `connected` is asserted by the client, but the resumed turn does not take
 * its word for it: the session it opens is built from the connections the
 * server can see, so a client claiming a connection that does not exist
 * resumes with nothing to execute.
 */
export const connectExternalAccountOutputSchema = z.object({
  status: z.enum(['connected', 'dismissed']),
  toolkit: z.string().min(1),
});

export type ConnectExternalAccountInput = z.infer<typeof connectExternalAccountInputSchema>;
export type ConnectExternalAccountOutput = z.infer<typeof connectExternalAccountOutputSchema>;

export function parseConnectExternalAccountInput(
  input: unknown
): ConnectExternalAccountInput | null {
  const parsed = connectExternalAccountInputSchema.safeParse(input);
  if (!parsed.success) return null;
  return { toolkit: parsed.data.toolkit.trim().toLowerCase() };
}

export function parseConnectExternalAccountOutput(
  output: unknown
): ConnectExternalAccountOutput | null {
  const parsed = connectExternalAccountOutputSchema.safeParse(output);
  if (!parsed.success) return null;

  return { status: parsed.data.status, toolkit: parsed.data.toolkit.trim().toLowerCase() };
}
